import { useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import type { HostContext } from "@repo/bridge";
import type { HostState } from "../bootstrap";
import { config } from "../config";

/** The slice of the cred manager identify needs — structural, so this hook stays off `@repo/auth`. */
interface IdentitySource {
  getValidToken(): Promise<string>;
  getActiveIdentity(): { userId: string; orgId: string } | undefined;
}

/**
 * Identifies the Gainsight PX user and account once creds are available, and again whenever the
 * host switches orgs. The client only knows user and org ids — no name or email — so that is all
 * identify carries, plus host platform/version. There is no logout to `reset` on: the host owns
 * the credentials and tears the whole SPA down with it.
 */
export function useGainsightIdentify(
  creds: IdentitySource,
  hostContext: HostContext,
  host: HostState,
): void {
  const channel = useMemo(() => hostContext.channel("org"), [hostContext]);
  const orgId = useSyncExternalStore(channel.subscribe, channel.get);
  const identifiedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Resolves creds for the active org (the cred manager switches it on an org update).
    creds
      .getValidToken()
      .then(() => {
        const identity = creds.getActiveIdentity();
        if (cancelled || !identity || !window.aptrinsic) return;
        const key = `${identity.userId}:${identity.orgId}`;
        if (identifiedKeyRef.current === key) return;
        identifiedKeyRef.current = key;
        window.aptrinsic(
          "identify",
          {
            id: identity.userId,
            hostPlatform: host.platform,
            hostVersion: host.version ?? "",
            ...(host.desktopVersion ? { hostDesktopVersion: host.desktopVersion } : {}),
            tigerAgentVersion: config.appVersion,
          },
          { id: identity.orgId },
        );
      })
      .catch(() => {
        // No creds (e.g. standalone without dev creds): nothing to identify.
      });
    return () => {
      cancelled = true;
    };
  }, [creds, host, orgId]);
}
